import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { adminAPI } from '../../services/adminApi';
import { useToast } from '../shared/Toast';
import { isKioskToggleLocked, kioskBlockedError } from '../../utils/pricingPhase';
import AddressAutocomplete, { loadGoogleMapsScript, AddressFields } from '../AddressAutocomplete';
import { LoadFailed } from '../settings/instantSettings';
import { SheetField, SheetSection } from '../settings/sheet';
import {
  ASIDE_BTN, Column, DANGER_GHOST_BTN, DataTable, DetailPanel, EmptyState, FilterSelect, GHOST_BTN, INPUT_CLS, ListPage,
  ListToolbar, Notice, PRIMARY_BTN, SELECT_CLS, StatusChip, matchesSearch,
} from '../shared/listPage';
import { Eye, EyeOff, Loader2, Lock, Monitor, Plus, Shuffle } from 'lucide-react';

// Φορτώνεται μόνο όταν ανοίξει — σπάνιο εργαλείο τεχνικού, δεν χρειάζεται στο αρχικό bundle.
const CashierDevices = React.lazy(() => import('../support/CashierDevices'));

/**
 * Super admin → «Καταστήματα» στη διάταξη «Πίνακας» (14/10/2026): αναζήτηση + φίλτρο κατάστασης, πίνακας,
 * και η φόρμα (νέο / επεξεργασία) σε συρτάρι με ενότητες «Φύλλου» αντί για παράθυρο με χρωματιστά κουτιά.
 * Κλικ στη γραμμή = επεξεργασία· η Απενεργοποίηση/Ενεργοποίηση είναι στο συρτάρι (ως τότε εικονίδια στη
 * γραμμή). Ίδια endpoints, ίδια σώματα, ίδιοι έλεγχοι και ίδια μηνύματα με πριν — μόνο η δομή άλλαξε.
 */

// Generate a random 6-digit PIN
export const generateRandomPin = (): string => {
  return String(Math.floor(100000 + Math.random() * 900000));
};

// Generate a random password (8 chars, letters+digits)
const generateRandomPassword = (): string => {
  const chars = 'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let pw = '';
  for (let i = 0; i < 8; i++) pw += chars[Math.floor(Math.random() * chars.length)];
  return pw;
};

interface StoreData {
  id: number;
  name: string;
  address: string;
  street_address?: string;
  street_number?: string;
  area?: string;
  country?: string;
  formatted_address?: string;
  latitude?: number;
  longitude?: number;
  phone_number: string;
  email: string;
  vat_number?: string;
  is_active: boolean;
  employee_count: number;
  active_employees: number;
  total_orders: number;
  recent_orders: number;
  created_at: string;
  is_branch?: boolean;
  branch_code?: string;
  doy?: string;
  activity?: string;
  city?: string;
  postal_code?: string;
  headquarters_address?: string;
  headquarters_street_address?: string;
  headquarters_street_number?: string;
  headquarters_city?: string;
  headquarters_postal_code?: string;
  headquarters_area?: string;
  headquarters_country?: string;
  headquarters_formatted_address?: string;
  headquarters_latitude?: number;
  headquarters_longitude?: number;
  ecr_provider?: string;
  training_mode_enabled?: boolean;
  caller_id_provider?: string;
  warehouse?: boolean;
  taxisnet_username?: string;
  taxisnet_password?: string;
  delivery?: boolean;
  delivery_self?: boolean;
  delivery_efood?: boolean;
  delivery_wolt?: boolean;
  takeaway?: boolean;
  dine_in?: boolean;
  // Xentrix Sala (κρατήσεις): stores.reservations — πύλη του /api/reservations
  reservations?: boolean;
  end_of_day?: string;
  kiosk_enabled?: boolean;
  analytics_tier?: string;
  barcode_enabled?: boolean;
  waste_enabled?: boolean;
  // Friendly label set by the org HQ + owning organization (JOINed in GET /stores)
  display_name?: string | null;
  organization_id?: number | null;
  organization_name?: string | null;
  // Remote support
  anydesk_id?: string | null;
  anydesk_password?: string | null;
}

// Token Oxygen του καταστήματος στην επεξεργασία: undefined = άγνωστο (φορτώνει ή απέτυχε — τότε δεν
// μπλοκάρει τίποτα). Ως 12/10/2026 η κατάσταση φαινόταν στο «ECR → Ρυθμίσεις» του καταστήματος.
type OxygenStatus = { active: boolean; masked: string | null } | undefined;

const maskToken = (token: string) => (token.length > 8 ? `${token.slice(0, 4)}••••${token.slice(-4)}` : '••••');

// Η PG στέλνει τα COUNT ως κείμενο.
const num = (v: unknown): number => {
  const n = parseInt(String(v ?? ''), 10);
  return Number.isFinite(n) ? n : 0;
};
const NUMBER = new Intl.NumberFormat('el-GR');
const count = (v: unknown): string => NUMBER.format(num(v));

// ─── Κομμάτια φόρμας που μοιράζεται και ο «Οργανισμοί» (γρήγορο νέο κατάστημα) ─────────────────────

/** Μέσα στο συρτάρι η πρώτη ενότητα κολλά στην κεφαλίδα του: χωρίς δική της γραμμή από πάνω. */
export const DRAWER_FORM_CLS = 'min-w-0 [&>section:first-child]:border-t-0 [&>section:first-child]:pt-0';

/** Τετράγωνο κουμπί δίπλα σε πεδίο (τυχαίο PIN/κωδικός), στο ύψος του INPUT_CLS. */
export const SIDE_BTN = 'inline-flex items-center justify-center w-11 h-11 shrink-0 rounded-xl border border-line-strong bg-surface-2 '
  + 'text-t2 hover:bg-surface-3 hover:text-t1 transition-colors disabled:opacity-50';

/** Αστερίσκος υποχρεωτικού πεδίου (το νόημα το λένε και τα μηνύματα της αποθήκευσης). */
export const RequiredMark: React.FC = () => <span aria-hidden="true" className="ml-1 text-red-600 dark:text-red-400">*</span>;

/**
 * Διακόπτης με την όψη του SettingsSwitch, αλλά πάνω σε εγγενές checkbox (role="switch"): τα κλικ, τα
 * tests (data-testid, toBeChecked/toBeDisabled) και ο φρουρός του kiosk μέσα στο onChange δουλεύουν όπως
 * με τα παλιά checkbox της φόρμας.
 */
export const SwitchControl: React.FC<{
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  labelId?: string;
  describedById?: string;
  testId?: string;
}> = ({ checked, onChange, disabled = false, labelId, describedById, testId }) => (
  <label className={`relative inline-flex shrink-0 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
    <input
      type="checkbox"
      role="switch"
      className="peer sr-only"
      checked={checked}
      disabled={disabled}
      aria-labelledby={labelId}
      aria-describedby={describedById}
      data-testid={testId}
      onChange={(e) => onChange(e.target.checked)}
    />
    <span
      aria-hidden="true"
      className={`relative block w-11 h-6 rounded-full transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-brand peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-surface-1 peer-disabled:opacity-50 ${
        checked ? 'bg-brand' : 'bg-surface-4 dark:bg-surface-3'
      }`}
    >
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-5' : ''}`} />
    </span>
  </label>
);

/**
 * Γραμμή «τίτλος/εξήγηση | διακόπτης» του Φύλλου (ίδιες κλάσεις με το SheetRow). `indent` για τις
 * υπο-επιλογές (π.χ. τρόποι delivery): μένουν διαδοχικές γραμμές, άρα με τη λεπτή γραμμή ανάμεσα.
 */
export const ToggleRow: React.FC<{
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  note?: React.ReactNode;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  indent?: boolean;
  testId?: string;
}> = ({ title, subtitle, note, checked, onChange, disabled, indent = false, testId }) => {
  const uid = useId();
  const labelId = `${uid}-label`;
  const subId = subtitle ? `${uid}-sub` : undefined;
  const noteId = note ? `${uid}-note` : undefined;
  const describedById = [subId, noteId].filter(Boolean).join(' ') || undefined;
  return (
    <div className={`xs-row${indent ? ' pl-6' : ''}`}>
      <div className="xs-row-text">
        <span id={labelId} className="xs-row-title">{title}</span>
        {subtitle && <p id={subId} className="xs-row-sub">{subtitle}</p>}
        {note && <p id={noteId} className="xs-row-note">{note}</p>}
      </div>
      <div className="xs-row-control">
        <SwitchControl checked={checked} onChange={onChange} disabled={disabled} labelId={labelId} describedById={describedById} testId={testId} />
      </div>
    </div>
  );
};

/** «Η ενεργοποίηση kiosk είναι κλειδωμένη…» κάτω από τον διακόπτη (φραγή ως τη Φ5). */
export const KioskLockedNote: React.FC = () => {
  const { t } = useTranslation(['admin']);
  return (
    <span className="inline-flex items-start gap-1.5">
      <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" aria-hidden="true" />
      <span>{t('admin:kioskActivationLocked')}</span>
    </span>
  );
};

// ─── Store form fields (shared between create & edit) ──────────────
interface StoreFormProps {
  data: any;
  setData: (fn: (prev: any) => any) => void;
  mode: 'create' | 'edit';
  // Η τιμή kiosk_enabled του καταστήματος ΟΠΩΣ ΦΟΡΤΩΘΗΚΕ (μόνο στην επεξεργασία)·
  // από αυτήν κρίνεται αν το checkbox κλειδώνει (φραγή ως τη Φ5).
  initialKioskEnabled?: boolean;
  oxygenStatus?: OxygenStatus;
  /** false στο support (25/10/2026): η λειτουργία εκπαίδευσης μένει στον super admin. */
  allowTrainingMode?: boolean;
}

export const StoreFormFields: React.FC<StoreFormProps> = ({ data, setData, mode, initialKioskEnabled, oxygenStatus, allowTrainingMode = true }) => {
  const { t } = useTranslation(['superAdminPanel', 'admin', 'common']);
  const set = (field: string, value: any) => setData((prev: any) => ({ ...prev, [field]: value }));
  const kioskLocked = isKioskToggleLocked(mode, initialKioskEnabled);
  const fid = (name: string) => `sa-store-${mode}-${name}`;
  const oxygen = data.ecr_provider === 'oxygen';

  return (
    <>
      {/* Πληροφορίες καταστήματος */}
      <SheetSection id={fid('info')} title={t('admin:storeFormInfoTitle')} hint={t('superAdminPanel:stores.infoHint')}>
        <div className="space-y-4">
          <SheetField label={<>{t('admin:name')}<RequiredMark /></>} htmlFor={fid('name')}>
            <input id={fid('name')} type="text" value={data.name} onChange={(e) => set('name', e.target.value)} className={INPUT_CLS}
              placeholder={t('admin:storeNamePlaceholder')} autoComplete="one-time-code" aria-required="true" />
          </SheetField>
          <AddressAutocomplete
            label={t('admin:address')}
            placeholder={t('admin:searchAddress')}
            value={{
              address: data.address, street_address: data.street_address, street_number: data.street_number,
              city: data.city, postal_code: data.postal_code, area: data.area, country: data.country,
              formatted_address: data.formatted_address, latitude: data.latitude, longitude: data.longitude
            }}
            onChange={(fields: AddressFields) => setData((prev: any) => ({
              ...prev,
              address: fields.address ?? prev.address, street_address: fields.street_address ?? prev.street_address,
              street_number: fields.street_number ?? prev.street_number, city: fields.city ?? prev.city,
              postal_code: fields.postal_code ?? prev.postal_code, area: fields.area ?? prev.area,
              country: fields.country ?? prev.country, formatted_address: fields.formatted_address ?? prev.formatted_address,
              latitude: fields.latitude ?? prev.latitude, longitude: fields.longitude ?? prev.longitude
            }))}
          />
          <div className="xs-grid2">
            <SheetField label={t('admin:phone')} htmlFor={fid('phone')}>
              <input id={fid('phone')} type="text" value={data.phone_number} onChange={(e) => set('phone_number', e.target.value)} className={INPUT_CLS}
                placeholder="+30 210 1234567" autoComplete="one-time-code" />
            </SheetField>
            <SheetField label={t('admin:email')} htmlFor={fid('email')}>
              <input id={fid('email')} type="email" value={data.email} onChange={(e) => set('email', e.target.value)} className={INPUT_CLS}
                placeholder="store@example.com" autoComplete="one-time-code" />
            </SheetField>
            <SheetField label={t('admin:vatNumberShort')} htmlFor={fid('vat')}>
              <input id={fid('vat')} type="text" value={data.vat_number} onChange={(e) => set('vat_number', e.target.value.replace(/\D/g, '').slice(0, 9))}
                className={INPUT_CLS} placeholder="123456789" autoComplete="off" />
            </SheetField>
            <SheetField label={t('admin:doy')} htmlFor={fid('doy')}>
              <input id={fid('doy')} type="text" value={data.doy} onChange={(e) => set('doy', e.target.value)} className={INPUT_CLS}
                placeholder={t('admin:doyPlaceholder')} autoComplete="one-time-code" />
            </SheetField>
          </div>
          <SheetField label={t('admin:activity')} htmlFor={fid('activity')}>
            <input id={fid('activity')} type="text" value={data.activity} onChange={(e) => set('activity', e.target.value)} className={INPUT_CLS}
              placeholder={t('admin:activityPlaceholder')} autoComplete="one-time-code" />
          </SheetField>
        </div>
      </SheetSection>

      {/* Υποκατάστημα */}
      <SheetSection id={fid('branch')} title={t('admin:branch')} hint={t('superAdminPanel:stores.branchHint')}>
        <ToggleRow
          title={t('admin:isBranch')}
          subtitle={t('admin:isBranchHint')}
          checked={!!data.is_branch}
          onChange={(next) => setData((prev: any) => ({ ...prev, is_branch: next, branch_code: next ? prev.branch_code : '' }))}
        />
        {data.is_branch && (
          <div className="mt-5 pt-5 border-t border-dashed border-line space-y-4">
            <SheetField label={<>{t('admin:branchCode')}<RequiredMark /></>} htmlFor={fid('branch-code')}>
              <input id={fid('branch-code')} type="text" value={data.branch_code} onChange={(e) => set('branch_code', e.target.value)}
                className={`${INPUT_CLS} max-w-[12rem]`} placeholder="1" autoComplete="one-time-code" aria-required="true" />
            </SheetField>
            <AddressAutocomplete
              label={t('admin:headquartersAddress')}
              placeholder={t('admin:searchHqAddress')}
              value={{
                address: data.headquarters_address, street_address: data.headquarters_street_address,
                street_number: data.headquarters_street_number, city: data.headquarters_city,
                postal_code: data.headquarters_postal_code, area: data.headquarters_area,
                country: data.headquarters_country, formatted_address: data.headquarters_formatted_address,
                latitude: data.headquarters_latitude, longitude: data.headquarters_longitude
              }}
              onChange={(fields: AddressFields) => setData((prev: any) => ({
                ...prev,
                headquarters_address: fields.address ?? prev.headquarters_address,
                headquarters_street_address: fields.street_address ?? prev.headquarters_street_address,
                headquarters_street_number: fields.street_number ?? prev.headquarters_street_number,
                headquarters_city: fields.city ?? prev.headquarters_city,
                headquarters_postal_code: fields.postal_code ?? prev.headquarters_postal_code,
                headquarters_area: fields.area ?? prev.headquarters_area,
                headquarters_country: fields.country ?? prev.headquarters_country,
                headquarters_formatted_address: fields.formatted_address ?? prev.headquarters_formatted_address,
                headquarters_latitude: fields.latitude ?? prev.headquarters_latitude,
                headquarters_longitude: fields.longitude ?? prev.headquarters_longitude
              }))}
              labelPrefix={t('admin:hqPrefix')}
            />
          </div>
        )}
      </SheetSection>

      {/* Login credentials — only for create */}
      {mode === 'create' && (
        <SheetSection id={fid('login')} title={t('admin:loginCredentials')} hint={t('superAdminPanel:stores.loginHint')}>
          <div className="xs-grid2">
            <SheetField label={<>{t('superAdminPanel:stores.username')}<RequiredMark /></>} htmlFor={fid('username')}>
              <input id={fid('username')} type="text" value={data.username} onChange={(e) => set('username', e.target.value)} className={INPUT_CLS}
                placeholder="store_user" autoComplete="one-time-code" aria-required="true" />
            </SheetField>
            <SheetField label={<>{t('admin:password')}<RequiredMark /></>} htmlFor={fid('password')}>
              <input id={fid('password')} type="password" value={data.password} onChange={(e) => set('password', e.target.value)} className={INPUT_CLS}
                placeholder={t('admin:storePasswordPlaceholder')} autoComplete="one-time-code" aria-required="true" />
            </SheetField>
          </div>
        </SheetSection>
      )}

      {/* ECR & φορολογικά */}
      <SheetSection id={fid('ecr')} title={t('admin:ecrFiscal')} hint={t('superAdminPanel:stores.ecrHint')}>
        <div className="space-y-4">
          <div className="xs-grid2">
            <SheetField label={t('admin:ecrProvider')} htmlFor={fid('ecr-provider')}>
              <select id={fid('ecr-provider')} value={data.ecr_provider} onChange={(e) => set('ecr_provider', e.target.value)} className={SELECT_CLS}>
                <option value="none">{t('admin:ecrNone')}</option>
                <option value="oxygen">Oxygen</option>
              </select>
            </SheetField>
            <SheetField label={t('admin:endOfDay')} htmlFor={fid('eod')}>
              <input id={fid('eod')} type="time" value={data.end_of_day} onChange={(e) => set('end_of_day', e.target.value)} className={INPUT_CLS} />
            </SheetField>
          </div>
          {oxygen && (
            <SheetField
              label={<>{t('admin:oxygenApiKey')}{mode === 'edit' && <span className="ml-1 font-normal text-t4">({t('admin:emptyKeepExisting')})</span>}</>}
              htmlFor={fid('oxygen-key')}
            >
              <input id={fid('oxygen-key')} type="text" value={data.oxygen_api_key} onChange={(e) => set('oxygen_api_key', e.target.value)}
                className={INPUT_CLS} placeholder="API Key" autoComplete="one-time-code"
                aria-describedby={mode === 'edit' && oxygenStatus ? fid('oxygen-status') : undefined} />
              {mode === 'edit' && oxygenStatus && (oxygenStatus.active && oxygenStatus.masked ? (
                <p id={fid('oxygen-status')} data-testid="oxygen-token-status" className="mt-1.5 text-xs text-t3">
                  {t('admin:oxygenTokenActive', { token: oxygenStatus.masked })}
                </p>
              ) : (
                // Χωρίς ενεργό token το Oxygen δεν εκδίδει τίποτα: προειδοποίηση, όχι απλή υπόδειξη.
                <div id={fid('oxygen-status')} className="mt-2">
                  <Notice testId="oxygen-token-status">{t('admin:oxygenTokenMissing')}</Notice>
                </div>
              ))}
            </SheetField>
          )}
          {/* Initial fiscal series — only meaningful at creation; afterwards
              series are managed per document type in ECR Settings. */}
          {oxygen && mode === 'create' && (
            <SheetField label={t('superAdminPanel:stores.initialSeries')} htmlFor={fid('series')} hint={t('superAdminPanel:stores.initialSeriesHint')}>
              <input
                id={fid('series')}
                type="number" min={1} max={999}
                value={data.initial_series}
                onChange={(e) => set('initial_series', Math.max(1, Math.min(999, parseInt(e.target.value) || 1)))}
                className={`${INPUT_CLS} max-w-[10rem]`}
                aria-describedby={`${fid('series')}-hint`}
              />
            </SheetField>
          )}
          {oxygen && allowTrainingMode && (
            <ToggleRow
              title={t('admin:trainingMode')}
              subtitle={t('admin:trainingModeHint')}
              checked={data.training_mode_enabled || false}
              onChange={(v) => set('training_mode_enabled', v)}
            />
          )}
          <div>
            <div className="xs-grid2">
              <SheetField label={t('admin:taxisnetUsername')} htmlFor={fid('taxisnet-user')}>
                <input id={fid('taxisnet-user')} type="text" value={data.taxisnet_username} onChange={(e) => set('taxisnet_username', e.target.value)}
                  className={INPUT_CLS} autoComplete="one-time-code" />
              </SheetField>
              <SheetField label={t('admin:taxisnetPassword')} htmlFor={fid('taxisnet-password')}>
                <input id={fid('taxisnet-password')} type="password" value={data.taxisnet_password} onChange={(e) => set('taxisnet_password', e.target.value)}
                  className={INPUT_CLS} placeholder={mode === 'edit' ? t('admin:emptyKeepPassword') : ''} autoComplete="one-time-code" />
              </SheetField>
            </div>
            <p className="xs-note">{t('admin:taxisnetHint')}</p>
          </div>
        </div>
      </SheetSection>

      {/* Remote support — προαιρετικά */}
      <SheetSection id={fid('support')} title={t('superAdminPanel:stores.supportTitle')} hint={t('superAdminPanel:stores.supportHint')}>
        <div className="xs-grid2">
          <SheetField label={t('superAdminPanel:stores.anydeskId')} htmlFor={fid('anydesk-id')}>
            <input id={fid('anydesk-id')} type="text" value={data.anydesk_id || ''} onChange={(e) => set('anydesk_id', e.target.value)} className={INPUT_CLS}
              placeholder={t('superAdminPanel:stores.anydeskIdPlaceholder')} autoComplete="one-time-code" />
          </SheetField>
          <SheetField label={t('superAdminPanel:stores.anydeskPassword')} htmlFor={fid('anydesk-password')}>
            <input id={fid('anydesk-password')} type="text" value={data.anydesk_password || ''} onChange={(e) => set('anydesk_password', e.target.value)}
              className={INPUT_CLS} autoComplete="one-time-code" />
          </SheetField>
        </div>
      </SheetSection>

      {/* Λειτουργίες του καταστήματος */}
      <SheetSection id={fid('features')} title={t('superAdminPanel:stores.featuresTitle')} hint={t('superAdminPanel:stores.featuresHint')}>
        <SheetField label={t('admin:callerIdLabel')} htmlFor={fid('caller-id')} className="max-w-sm">
          <select
            id={fid('caller-id')}
            value={data.caller_id_provider || 'none'}
            onChange={(e) => set('caller_id_provider', e.target.value)}
            className={SELECT_CLS}
          >
            <option value="none">{t('admin:callerIdNone')}</option>
            <option value="yuboto">{t('admin:callerIdYuboto')}</option>
            <option value="usrobotics">{t('admin:callerIdUsrobotics')}</option>
          </select>
        </SheetField>
        <div className="mt-6">
          <ToggleRow title={t('admin:warehouseMode')} subtitle={t('admin:stockTracking')} checked={!!data.warehouse} onChange={(v) => set('warehouse', v)} />
          <ToggleRow title={t('admin:delivery')} subtitle={t('superAdminPanel:stores.deliveryHint')} checked={!!data.delivery} onChange={(v) => set('delivery', v)} />
          {data.delivery && (
            <>
              <ToggleRow indent title={t('admin:ownDelivery')} checked={!!data.delivery_self} onChange={(v) => set('delivery_self', v)} />
              <ToggleRow indent title="e-food" checked={!!data.delivery_efood} onChange={(v) => set('delivery_efood', v)} />
              <ToggleRow indent title="Wolt" checked={!!data.delivery_wolt} onChange={(v) => set('delivery_wolt', v)} />
            </>
          )}
          <ToggleRow title={t('superAdminPanel:stores.takeaway')} subtitle={t('admin:ordersForPickup')} checked={!!data.takeaway} onChange={(v) => set('takeaway', v)} />
          <ToggleRow title={t('superAdminPanel:stores.dineIn')} subtitle={t('admin:inStoreOrders')} checked={!!data.dine_in} onChange={(v) => set('dine_in', v)} />
          <ToggleRow
            testId="store-reservations-toggle"
            title={t('admin:salaReservations')}
            subtitle={t('admin:salaReservationsHint')}
            checked={!!data.reservations}
            onChange={(v) => set('reservations', v)}
          />
          {/* Κλειδωμένο μόνο για ΕΝΕΡΓΟΠΟΙΗΣΗ: ό,τι είναι ήδη ενεργό μένει ελεύθερο για απενεργοποίηση.
              Ο έλεγχος και στο onChange: το disabled μόνο του δεν φτάνει παντού (π.χ. συνθετικό click). */}
          <ToggleRow
            testId="store-kiosk-toggle"
            title={t('superAdminPanel:stores.kiosk')}
            subtitle={t('admin:selfService')}
            note={kioskLocked ? <KioskLockedNote /> : undefined}
            checked={!!data.kiosk_enabled}
            disabled={kioskLocked}
            onChange={(v) => { if (!kioskLocked) set('kiosk_enabled', v); }}
          />
          <ToggleRow
            title={t('superAdminPanel:stores.premiumAnalytics')}
            subtitle={t('admin:premiumAnalytics')}
            checked={data.analytics_tier === 'premium'}
            onChange={(v) => set('analytics_tier', v ? 'premium' : 'basic')}
          />
          <ToggleRow title={t('admin:barcodeProducts')} subtitle={t('admin:barcodeHint')} checked={data.barcode_enabled || false} onChange={(v) => set('barcode_enabled', v)} />
          <ToggleRow title={t('superAdminPanel:stores.waste')} subtitle={t('superAdminPanel:stores.wasteHint')} checked={data.waste_enabled || false} onChange={(v) => set('waste_enabled', v)} />
        </div>
      </SheetSection>
    </>
  );
};

/** Κενή φόρμα νέου καταστήματος — κοινή με το «Νέο κατάστημα» του support (25/10/2026). */
export const EMPTY_STORE_FORM = {
  name: '', address: '', street_address: '', street_number: '', area: '', country: 'GR',
  formatted_address: '', latitude: null as number | null, longitude: null as number | null,
  phone_number: '', email: '', vat_number: '', username: '', password: '',
  is_branch: false, branch_code: '', doy: '', activity: '', city: '', postal_code: '',
  headquarters_address: '', headquarters_street_address: '', headquarters_street_number: '',
  headquarters_city: '', headquarters_postal_code: '', headquarters_area: '',
  headquarters_country: 'GR', headquarters_formatted_address: '',
  headquarters_latitude: null as number | null, headquarters_longitude: null as number | null,
  ecr_provider: 'none' as 'none' | 'oxygen', training_mode_enabled: false, oxygen_api_key: '',
  initial_series: 1,
  caller_id_provider: 'none' as 'none' | 'yuboto' | 'usrobotics',
  // Warehouse module is opt-in; TAXISnet prefilled with the chain-wide account.
  warehouse: false, taxisnet_username: 'PCFACTORY-160525', taxisnet_password: 'afm801436888',
  delivery: true, delivery_self: true, delivery_efood: false, delivery_wolt: false,
  takeaway: true, dine_in: false, reservations: false, end_of_day: '03:00', kiosk_enabled: false,
  analytics_tier: 'basic', barcode_enabled: false, waste_enabled: false,
  anydesk_id: '', anydesk_password: ''
};

/**
 * «Νέο κατάστημα» ήδη συμπληρωμένο — από τις Πωλήσεις, όταν ένας υποψήφιος γίνεται πελάτης (3/11/2026). Ανοίγει η ίδια
 * φόρμα με τον ίδιο έλεγχο· μετά τη δημιουργία, το onCreated δένει τον υποψήφιο με το κατάστημα και γυρίζει το μήνυμα.
 * Νέο nonce = ξανάνοιγμα (και για τον ίδιο υποψήφιο).
 */
export interface StorePrefill {
  nonce: number;
  store: Partial<typeof EMPTY_STORE_FORM>;
  manager: { first_name?: string; last_name?: string; email?: string; phone?: string };
  onCreated: (storeId: number, storeName: string) => Promise<{ ok: boolean; message: string }>;
}

// ─── Main Stores Tab ───────────────────────────────────────────────
type ActiveFilter = 'all' | 'active' | 'inactive';

const SuperAdminStoresTab: React.FC<{ prefill?: StorePrefill | null }> = ({ prefill = null }) => {
  const { t } = useTranslation(['superAdminPanel', 'admin', 'common']);
  const { showToast } = useToast();
  const [stores, setStores] = useState<StoreData[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState<ActiveFilter>('all');

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingStore, setEditingStore] = useState<StoreData | null>(null);

  const emptyStore = EMPTY_STORE_FORM;

  const [newStore, setNewStore] = useState(emptyStore);
  const [newManager, setNewManager] = useState({ first_name: '', last_name: '', email: '', phone: '', pin_code: generateRandomPin() });
  const [showResetPassword, setShowResetPassword] = useState(false);
  // «Συσκευές ταμείου» (24/10/2026): ίδιο παράθυρο με το πάνελ support — οθόνη πελάτη + modem ανά PC.
  const [showDevices, setShowDevices] = useState(false);
  const [resetPassword, setResetPassword] = useState('');
  const [showResetPw, setShowResetPw] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [editStore, setEditStore] = useState(emptyStore);
  const [oxygenStatus, setOxygenStatus] = useState<OxygenStatus>(undefined);
  const oxygenStatusFor = useRef<number | null>(null);

  useEffect(() => {
    fetchStores();
    // Μία φορά στο άνοιγμα της σελίδας, όπως πριν.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Από τις Πωλήσεις: η φόρμα ανοίγει με τα στοιχεία του υποψηφίου (το PIN του διευθυντή φτιάχνεται εδώ, όπως πάντα).
  const pendingPrefillRef = useRef<StorePrefill | null>(null);
  useEffect(() => {
    if (!prefill) return;
    pendingPrefillRef.current = prefill;
    setNewStore({ ...EMPTY_STORE_FORM, ...prefill.store });
    setNewManager({ first_name: '', last_name: '', email: '', phone: '', ...prefill.manager, pin_code: generateRandomPin() });
    setShowCreateModal(true);
    // Μόνο όταν έρχεται νέο αίτημα (nonce), όχι σε κάθε render του γονέα.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill?.nonce]);
  const openCreate = () => { pendingPrefillRef.current = null; setShowCreateModal(true); };
  // «Άκυρο» σε συμπληρωμένη φόρμα: τα στοιχεία του υποψηφίου φεύγουν (το πρόχειρο μιας κανονικής φόρμας μένει, όπως πριν).
  const closeCreate = () => {
    if (pendingPrefillRef.current) {
      setNewStore(EMPTY_STORE_FORM);
      setNewManager({ first_name: '', last_name: '', email: '', phone: '', pin_code: generateRandomPin() });
    }
    pendingPrefillRef.current = null;
    setShowCreateModal(false);
  };

  useEffect(() => {
    if ((showCreateModal || showEditModal) && !(window as any).google) {
      loadGoogleMapsScript();
    }
  }, [showCreateModal, showEditModal]);

  const fetchStores = async () => {
    try {
      setLoading(true);
      const response: any = await adminAPI.getStores();
      setStores(response);
      setLoadFailed(false);
    } catch (error) {
      console.error('Failed to fetch stores:', error);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  };

  // Η κατάσταση στο ανοιχτό συρτάρι αλλάζει αμέσως (το κουμπί γίνεται «Ενεργοποίηση»/«Απενεργοποίηση»),
  // χωρίς να χαθεί ό,τι γράφτηκε στη φόρμα.
  const markActive = (storeId: number, isActive: boolean) => {
    setEditingStore(prev => (prev && prev.id === storeId ? { ...prev, is_active: isActive } : prev));
  };

  const handleDeleteStore = async (storeId: number, storeName: string) => {
    if (window.confirm(t('admin:confirmDeactivateStore', { name: storeName }))) {
      try {
        await adminAPI.deleteStore(storeId);
        markActive(storeId, false);
        await fetchStores();
      } catch (error) {
        console.error('Failed:', error);
        alert(t('admin:failedToDeactivate'));
      }
    }
  };

  const handleActivateStore = async (storeId: number, storeName: string) => {
    if (window.confirm(t('admin:confirmActivateStore', { name: storeName }))) {
      try {
        await adminAPI.updateStore(storeId, { is_active: true });
        markActive(storeId, true);
        await fetchStores();
      } catch (error) {
        console.error('Failed:', error);
        alert(t('admin:failedToActivate'));
      }
    }
  };

  // Ενεργό token Oxygen (GET /api/admin/stores/:id/oxygen-credentials)· στη φόρμα μόνο μασκαρισμένο.
  const loadOxygenStatus = async (storeId: number) => {
    oxygenStatusFor.current = storeId;
    setOxygenStatus(undefined);
    try {
      const res: any = await adminAPI.getOxygenCredentials(storeId);
      if (oxygenStatusFor.current !== storeId) return;
      const token = typeof res?.api_token === 'string' ? res.api_token : '';
      setOxygenStatus({ active: token !== '' && res?.is_active !== false, masked: token ? maskToken(token) : null });
    } catch (error) {
      console.error('Failed to fetch Oxygen credentials:', error);
    }
  };

  const handleEditStore = (store: StoreData) => {
    loadOxygenStatus(store.id);
    setEditingStore(store);
    setShowResetPassword(false);
    setEditStore({
      name: store.name || '', address: store.address || '',
      street_address: store.street_address || '', street_number: store.street_number || '',
      area: store.area || '', country: store.country || 'GR',
      formatted_address: store.formatted_address || '',
      latitude: store.latitude || null, longitude: store.longitude || null,
      phone_number: store.phone_number || '', email: store.email || '',
      vat_number: store.vat_number || '', username: '', password: '',
      is_branch: store.is_branch || false, branch_code: store.branch_code || '',
      doy: store.doy || '', activity: store.activity || '',
      city: store.city || '', postal_code: store.postal_code || '',
      headquarters_address: store.headquarters_address || '',
      headquarters_street_address: store.headquarters_street_address || '',
      headquarters_street_number: store.headquarters_street_number || '',
      headquarters_city: store.headquarters_city || '',
      headquarters_postal_code: store.headquarters_postal_code || '',
      headquarters_area: store.headquarters_area || '',
      headquarters_country: store.headquarters_country || 'GR',
      headquarters_formatted_address: store.headquarters_formatted_address || '',
      headquarters_latitude: store.headquarters_latitude || null,
      headquarters_longitude: store.headquarters_longitude || null,
      ecr_provider: (store.ecr_provider as 'none' | 'oxygen') || 'none',
      training_mode_enabled: store.training_mode_enabled || false,
      oxygen_api_key: '',
      initial_series: 1, // creation-only; series are managed in ECR Settings afterwards
      caller_id_provider: (store.caller_id_provider as 'none' | 'yuboto' | 'usrobotics') || 'none',
      warehouse: store.warehouse !== false,
      taxisnet_username: store.taxisnet_username || '',
      taxisnet_password: store.taxisnet_password || '',
      delivery: store.delivery !== false,
      delivery_self: store.delivery_self !== false,
      delivery_efood: store.delivery_efood || false,
      delivery_wolt: store.delivery_wolt || false,
      takeaway: store.takeaway !== false,
      dine_in: store.dine_in || false,
      reservations: store.reservations || false,
      end_of_day: store.end_of_day || '03:00',
      kiosk_enabled: store.kiosk_enabled || false,
      analytics_tier: store.analytics_tier || 'basic',
      barcode_enabled: store.barcode_enabled || false, waste_enabled: store.waste_enabled || false,
      anydesk_id: store.anydesk_id || '',
      anydesk_password: store.anydesk_password || ''
    });
    setShowEditModal(true);
  };

  const closeEdit = () => {
    setShowEditModal(false);
    setEditingStore(null);
    setShowResetPassword(false);
  };

  const handleSaveStore = async () => {
    if (!editingStore) return;
    if (!editStore.name.trim()) { alert(t('admin:storeNameRequired')); return; }
    // Oxygen χωρίς ενεργό token = κανένα παραστατικό (fiscalDocumentService: is_active = true).
    if (editStore.ecr_provider === 'oxygen' && !editStore.oxygen_api_key.trim() && oxygenStatus && !oxygenStatus.active) {
      alert(t('admin:oxygenTokenRequired'));
      return;
    }
    try {
      setSaving(true);
      // Κενός κωδικός TAXISnet = «διατήρηση» (όπως λέει το πεδίο), όχι σβήσιμο του αποθηκευμένου.
      const { oxygen_api_key, username, password, taxisnet_password, ...rest } = editStore;
      const storeData = taxisnet_password ? { ...rest, taxisnet_password } : rest;
      await adminAPI.updateStore(editingStore.id, storeData);
      if (editStore.ecr_provider === 'oxygen' && oxygen_api_key) {
        await adminAPI.saveOxygenCredentials(editingStore.id, oxygen_api_key);
      }
      setShowEditModal(false);
      setEditingStore(null);
      await fetchStores();
    } catch (error: any) {
      console.error('Failed:', error);
      // 409 της φραγής kiosk (φόρμα ανοιχτή πριν το deploy ή αίτημα από το πλάι):
      // toast με το μήνυμα του server, η φόρμα μένει ανοιχτή για διόρθωση.
      const kioskMsg = kioskBlockedError(error);
      if (kioskMsg) showToast('error', kioskMsg, 8000);
      else alert(error.response?.data?.error || t('admin:failedToUpdate'));
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetPassword || resetPassword.length < 6) { alert(t('admin:passwordMinChangeError')); return; }
    if (!window.confirm(t('admin:confirmPasswordChange', { name: editingStore?.name }))) return;
    try {
      setResettingPassword(true);
      await adminAPI.resetStorePassword(editingStore!.id, resetPassword);
      alert(t('admin:passwordChangedSuccess'));
      setShowResetPassword(false);
      setResetPassword('');
    } catch (err: any) {
      alert(err.response?.data?.error || t('admin:failedToChangePassword'));
    } finally {
      setResettingPassword(false);
    }
  };

  const handleCreateStore = async () => {
    if (!newStore.name.trim()) { alert(t('admin:storeNameRequired')); return; }
    if (!newStore.username.trim()) { alert(t('admin:usernameRequired')); return; }
    if (!newStore.password.trim() || newStore.password.length < 6) { alert(t('admin:passwordMinError')); return; }
    // Κατάστημα σε Oxygen χωρίς token δεν εκδίδει κανένα παραστατικό.
    if (newStore.ecr_provider === 'oxygen' && !newStore.oxygen_api_key.trim()) { alert(t('admin:oxygenTokenRequired')); return; }
    if (!newManager.first_name.trim() || !newManager.last_name.trim()) { alert(t('admin:managerNameRequired')); return; }
    if (!newManager.email.trim()) { alert(t('admin:managerEmailRequired')); return; }
    if (!newManager.pin_code.trim() || newManager.pin_code.length !== 6) { alert(t('admin:managerPinError')); return; }

    try {
      setCreating(true);
      const { oxygen_api_key, ...storeData } = newStore;
      // Ο διευθυντής ΜΑΖΙ με το κατάστημα σε ένα αίτημα (16/10/2026): ο server τα γράφει σε μία συναλλαγή και
      // αρνείται πριν από οτιδήποτε αν το email υπάρχει ήδη. Ως τότε ήταν δεύτερο αίτημα, και όταν έπεφτε
      // έμενε κατάστημα χωρίς διαχειριστή (κατ. 43 Κουλουράδων).
      const storeResponse: any = await adminAPI.createStore({ ...storeData, manager: newManager });
      const storeId = storeResponse.id;

      if (newStore.ecr_provider === 'oxygen' && oxygen_api_key) {
        await adminAPI.saveOxygenCredentials(storeId, oxygen_api_key);
      }

      setNewStore(emptyStore);
      setNewManager({ first_name: '', last_name: '', email: '', phone: '', pin_code: '' });
      setShowCreateModal(false);
      const pending = pendingPrefillRef.current;
      pendingPrefillRef.current = null;
      if (pending) {
        const linked = await pending.onCreated(storeId, storeData.name);
        showToast(linked.ok ? 'success' : 'error', linked.message, linked.ok ? 4000 : 8000);
      }
      await fetchStores();
    } catch (error: any) {
      console.error('Failed:', error);
      const kioskMsg = kioskBlockedError(error);
      if (kioskMsg) showToast('error', kioskMsg, 8000);
      else alert(error.response?.data?.error || t('admin:failedToCreate'));
    } finally {
      setCreating(false);
    }
  };

  // Αναζήτηση στη μνήμη, χωρίς τόνους και πεζά (όπως οι Υπάλληλοι): «κουλουρι» βρίσκει το «ΚΟΥΛΟΎΡΙ».
  const filteredStores = stores.filter(store => {
    const matches = matchesSearch(searchTerm, store.name, store.display_name, store.organization_name, store.address, store.id);
    const matchesFilter = filterActive === 'all' || (filterActive === 'active' && store.is_active) || (filterActive === 'inactive' && !store.is_active);
    return matches && matchesFilter;
  });

  const activeCount = stores.filter(s => s.is_active).length;
  const inactiveCount = stores.length - activeCount;
  const filtersActive = searchTerm.trim() !== '' || filterActive !== 'all';

  const columns = useMemo<Column<StoreData>[]>(() => [
    {
      key: 'name',
      header: t('superAdminPanel:stores.colStore'),
      width: 'minmax(220px, 2fr)',
      sortable: true,
      sortValue: s => s.display_name || s.name,
      // Πολλά καταστήματα μοιράζονται την ίδια επωνυμία — δείξε το φιλικό display_name
      // (+ οργανισμό) και την επωνυμία από κάτω.
      render: s => (
        <>
          <span className="block font-medium">
            <span>{s.display_name || s.name}</span>
            {s.organization_name && <span className="font-normal text-t3"> ({s.organization_name})</span>}
          </span>
          <span className="block text-xs text-t3">#{s.id}{s.display_name ? ` · ${s.name}` : ''}</span>
        </>
      ),
      // Στο κινητό ο τίτλος μόνο το όνομα· ο οργανισμός πάει στη γραμμή από κάτω (αλλιώς κόβεται).
      stackRender: s => s.display_name || s.name,
    },
    {
      key: 'contact',
      header: t('admin:contact2'),
      width: 'minmax(180px, 1.6fr)',
      render: s => (
        <>
          <span className="block text-t2">{s.address || '—'}</span>
          <span className="block text-xs text-t3">{s.phone_number || '—'}</span>
        </>
      ),
      stackRender: s => [`#${s.id}`, s.organization_name, s.address].filter(Boolean).join(' · '),
    },
    {
      key: 'employees',
      header: t('admin:employees'),
      width: '120px',
      align: 'right',
      hideBelow: 'medium',
      sortable: true,
      sortValue: s => num(s.active_employees),
      render: s => <span className="text-t2">{num(s.active_employees)}/{num(s.employee_count)}</span>,
    },
    {
      key: 'orders',
      header: t('admin:orders'),
      width: '140px',
      align: 'right',
      hideBelow: 'medium',
      sortable: true,
      sortValue: s => num(s.total_orders),
      render: s => (
        <>
          <span className="block text-t2">{count(s.total_orders)}</span>
          <span className="block text-xs text-t3">{t('superAdminPanel:stores.recentOrders', { n: count(s.recent_orders) })}</span>
        </>
      ),
    },
    {
      key: 'status',
      header: t('admin:statusLabel'),
      width: '120px',
      stack: 'end',
      render: s => (s.is_active
        ? <StatusChip tone="ok">{t('admin:activeStatus')}</StatusChip>
        : <StatusChip tone="muted">{t('admin:inactiveStatus')}</StatusChip>),
    },
  ], [t]);

  const firstLoad = loading && stores.length === 0;
  const summary = firstLoad || (loadFailed && stores.length === 0)
    ? null
    : filtersActive
      ? t('superAdminPanel:stores.filtered', { shown: filteredStores.length, total: stores.length })
      : `${t('superAdminPanel:stores.count', { count: stores.length })} · ${t('superAdminPanel:stores.active', { count: activeCount })}`;

  const managerField = (key: 'first_name' | 'last_name' | 'email' | 'phone', label: React.ReactNode, type = 'text') => (
    <SheetField label={label} htmlFor={`sa-store-manager-${key}`}>
      <input
        id={`sa-store-manager-${key}`}
        type={type}
        value={newManager[key]}
        onChange={(e) => setNewManager({ ...newManager, [key]: e.target.value })}
        className={INPUT_CLS}
        autoComplete="one-time-code"
      />
    </SheetField>
  );

  return (
    <ListPage
      eyebrow={t('superAdminPanel:nav.groups.customers')}
      title={t('admin:stores')}
      subtitle={t('superAdminPanel:stores.subtitle')}
      testId="sa-stores"
    >
      <ListToolbar
        search={{
          value: searchTerm,
          onChange: setSearchTerm,
          placeholder: t('admin:searchByNameAddressId'),
          ariaLabel: t('superAdminPanel:stores.searchLabel'),
        }}
        filters={(
          <FilterSelect
            label={t('admin:statusLabel')}
            value={filterActive === 'all' ? '' : filterActive}
            onChange={(v) => setFilterActive((v || 'all') as ActiveFilter)}
            allLabel={`${t('admin:allFilter')} (${stores.length})`}
            options={[
              { value: 'active', label: `${t('admin:activeFilter')} (${activeCount})` },
              { value: 'inactive', label: `${t('admin:inactiveFilter')} (${inactiveCount})` },
            ]}
            testId="sa-stores-status-filter"
          />
        )}
        actions={(
          <button type="button" className={PRIMARY_BTN} onClick={openCreate}>
            <Plus className="w-4 h-4" aria-hidden="true" />
            {t('admin:newStore')}
          </button>
        )}
        summary={summary}
      />

      {loadFailed && stores.length === 0 ? (
        <LoadFailed message={t('superAdminPanel:stores.loadFailed')} onRetry={fetchStores} />
      ) : (
        <DataTable
          columns={columns}
          rows={filteredStores}
          rowKey={s => s.id}
          onRowClick={handleEditStore}
          selectedKey={showEditModal ? editingStore?.id ?? null : null}
          loading={loading}
          stickyHeader
          rowLabel={s => t('superAdminPanel:stores.rowLabel', { name: s.display_name || s.name, id: s.id })}
          empty={(
            <EmptyState
              title={t('admin:noStores')}
              hint={filtersActive ? t('superAdminPanel:stores.emptyHint') : undefined}
            />
          )}
          testId="sa-stores-table"
        />
      )}

      {/* ─── Νέο κατάστημα ─────────────────────────────────────────── */}
      <DetailPanel
        open={showCreateModal}
        onClose={closeCreate}
        title={t('admin:createNewStore')}
        subtitle={t('admin:createStoreAndManager')}
        width="lg"
        testId="sa-store-create"
        footer={(
          <>
            <button type="button" className={GHOST_BTN} onClick={closeCreate} disabled={creating}>{t('common:cancel')}</button>
            <button type="button" className={PRIMARY_BTN} onClick={handleCreateStore} disabled={creating}>
              {creating
                ? <><Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> {t('admin:creating2')}</>
                : <><Plus className="w-4 h-4" aria-hidden="true" />{t('admin:create')}</>}
            </button>
          </>
        )}
      >
        <div className={DRAWER_FORM_CLS}>
          <StoreFormFields data={newStore} setData={setNewStore} mode="create" />

          {/* Διευθυντής καταστήματος */}
          <SheetSection id="sa-store-create-manager" title={t('admin:storeManager')} hint={t('superAdminPanel:stores.managerHint')}>
            <div className="xs-grid2">
              {managerField('first_name', <>{t('admin:firstName')}<RequiredMark /></>)}
              {managerField('last_name', <>{t('admin:lastName')}<RequiredMark /></>)}
              {managerField('email', <>{t('admin:email')}<RequiredMark /></>, 'email')}
              {managerField('phone', t('admin:phone'))}
            </div>
            <SheetField label={t('admin:pinDigitsLabel')} htmlFor="sa-store-manager-pin" className="mt-4">
              <div className="flex gap-2 max-w-xs">
                <input
                  id="sa-store-manager-pin"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={newManager.pin_code}
                  onChange={(e) => setNewManager({ ...newManager, pin_code: e.target.value.replace(/\D/g, '') })}
                  className={INPUT_CLS}
                  placeholder="123456"
                  autoComplete="one-time-code"
                />
                <button
                  type="button"
                  onClick={() => setNewManager({ ...newManager, pin_code: generateRandomPin() })}
                  className={SIDE_BTN}
                  title={t('superAdminPanel:stores.randomPin')}
                  aria-label={t('superAdminPanel:stores.randomPin')}
                >
                  <Shuffle className="w-4 h-4" aria-hidden="true" />
                </button>
              </div>
              <p className="mt-1.5 text-xs text-t2 font-mono select-all">{newManager.pin_code}</p>
            </SheetField>
          </SheetSection>
        </div>
      </DetailPanel>

      {/* ─── Επεξεργασία καταστήματος ──────────────────────────────── */}
      <DetailPanel
        open={showEditModal && editingStore !== null}
        onClose={closeEdit}
        title={editingStore ? (editingStore.display_name || editingStore.name) : ''}
        subtitle={editingStore
          ? [`#${editingStore.id}`, editingStore.display_name ? editingStore.name : null, editingStore.organization_name].filter(Boolean).join(' · ')
          : undefined}
        width="lg"
        testId="sa-store-edit"
        footerStart={editingStore && (editingStore.is_active ? (
          <button type="button" className={DANGER_GHOST_BTN} onClick={() => handleDeleteStore(editingStore.id, editingStore.name)} disabled={saving}>
            {t('admin:deactivate')}
          </button>
        ) : (
          <button type="button" className={GHOST_BTN} onClick={() => handleActivateStore(editingStore.id, editingStore.name)} disabled={saving}>
            {t('admin:activate')}
          </button>
        ))}
        footer={(
          <>
            <button type="button" className={GHOST_BTN} onClick={closeEdit} disabled={saving}>{t('common:cancel')}</button>
            <button type="button" className={PRIMARY_BTN} onClick={handleSaveStore} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
              {saving ? t('admin:saving') : t('admin:save')}
            </button>
          </>
        )}
      >
        {editingStore && (
          <div className={DRAWER_FORM_CLS}>
            <StoreFormFields data={editStore} setData={setEditStore} mode="edit" initialKioskEnabled={editingStore?.kiosk_enabled === true} oxygenStatus={oxygenStatus} />

            {/* Συσκευές ταμείου */}
            <SheetSection id="sa-store-edit-devices" title={t('superAdminPanel:stores.devicesTitle')} hint={t('superAdminPanel:stores.devicesHint')}>
              <button type="button" className={ASIDE_BTN} onClick={() => setShowDevices(true)} data-testid="sa-store-open-devices">
                <Monitor className="w-4 h-4" aria-hidden="true" /> {t('superAdminPanel:stores.devicesOpen')}
              </button>
            </SheetSection>

            {/* Επαναφορά κωδικού σύνδεσης */}
            <SheetSection id="sa-store-edit-password" title={t('admin:resetLoginPassword')} hint={t('superAdminPanel:stores.passwordHint')}>
              <button
                type="button"
                className={ASIDE_BTN}
                aria-expanded={showResetPassword}
                onClick={() => { setShowResetPassword(!showResetPassword); setResetPassword(generateRandomPassword()); }}
              >
                {showResetPassword ? t('admin:hideLabel') : t('admin:changePassword')}
              </button>
              {showResetPassword && (
                <div className="mt-4 space-y-3">
                  <SheetField label={t('superAdminPanel:stores.newPassword')} htmlFor="sa-store-reset-password">
                    <div className="flex gap-2 max-w-md">
                      <div className="relative flex-1 min-w-0">
                        <input
                          id="sa-store-reset-password"
                          type={showResetPw ? 'text' : 'password'}
                          value={resetPassword}
                          onChange={(e) => setResetPassword(e.target.value)}
                          className={`${INPUT_CLS} pr-12`}
                          placeholder={t('admin:newPasswordPlaceholder')}
                          autoComplete="one-time-code"
                        />
                        <button
                          type="button"
                          onClick={() => setShowResetPw(!showResetPw)}
                          aria-label={showResetPw ? t('superAdminPanel:stores.hidePassword') : t('superAdminPanel:stores.showPassword')}
                          title={showResetPw ? t('superAdminPanel:stores.hidePassword') : t('superAdminPanel:stores.showPassword')}
                          className="absolute right-1.5 top-1/2 -translate-y-1/2 w-9 h-9 rounded-lg flex items-center justify-center text-t3 hover:text-t1 hover:bg-surface-3 transition-colors"
                        >
                          {showResetPw ? <EyeOff className="w-4 h-4" aria-hidden="true" /> : <Eye className="w-4 h-4" aria-hidden="true" />}
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={() => setResetPassword(generateRandomPassword())}
                        className={SIDE_BTN}
                        title={t('superAdminPanel:stores.randomPassword')}
                        aria-label={t('superAdminPanel:stores.randomPassword')}
                      >
                        <Shuffle className="w-4 h-4" aria-hidden="true" />
                      </button>
                    </div>
                    {showResetPw && resetPassword && <p className="mt-1.5 text-xs text-t2 font-mono select-all">{resetPassword}</p>}
                  </SheetField>
                  <button
                    type="button"
                    onClick={handleResetPassword}
                    disabled={resettingPassword || !resetPassword}
                    className={DANGER_GHOST_BTN}
                  >
                    {resettingPassword
                      ? <><Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> {t('admin:changingPassword')}</>
                      : <><Lock className="w-4 h-4" aria-hidden="true" /> {t('admin:changePasswordBtn')}</>}
                  </button>
                </div>
              )}
            </SheetSection>
          </div>
        )}
      </DetailPanel>
      {showDevices && editingStore && (
        <React.Suspense fallback={null}>
          <CashierDevices storeId={editingStore.id} storeName={editingStore.display_name || editingStore.name} onClose={() => setShowDevices(false)} />
        </React.Suspense>
      )}
    </ListPage>
  );
};

export default SuperAdminStoresTab;
